interface ErrorLog {
  id: string;
  timestamp: string;
  level: 'error' | 'warning' | 'info';
  message: string;
  stack?: string;
  url: string;
  userAgent: string;
  userId?: string;
  context?: string;
  componentStack?: string;
  additionalData?: Record<string, any>;
}

class ErrorLogger {
  private logs: ErrorLog[] = [];
  private maxLogs = 100;
  private isProduction = process.env.NODE_ENV === 'production';

  /**
   * Log an error to console and store for potential reporting
   */
  logError(error: Error | string, context?: string, additionalData?: Record<string, any>) {
    const errorLog = this.createErrorLog(error, 'error', context, additionalData);
    this.addLog(errorLog);

    // Always log to console in development
    if (!this.isProduction) {
      console.error('Error logged:', errorLog);
    }
  }

  /**
   * Log a warning
   */
  logWarning(message: string, context?: string, additionalData?: Record<string, any>) {
    const errorLog = this.createErrorLog(message, 'warning', context, additionalData);
    this.addLog(errorLog);

    if (!this.isProduction) {
      console.warn('Warning logged:', errorLog);
    }
  }

  /**
   * Log info message
   */
  logInfo(message: string, context?: string, additionalData?: Record<string, any>) {
    const errorLog = this.createErrorLog(message, 'info', context, additionalData);
    this.addLog(errorLog);

    if (!this.isProduction) {
      console.info('Info logged:', errorLog);
    }
  }

  /**
   * Log React Error Boundary errors
   */
  logErrorBoundary(
    error: Error, 
    errorInfo: { componentStack: string }, 
    context: string = 'ErrorBoundary'
  ) {
    const errorLog = this.createErrorLog(
      error, 
      'error', 
      context, 
      {
        componentStack: errorInfo.componentStack,
        errorBoundary: true
      }
    );
    
    errorLog.componentStack = errorInfo.componentStack;
    this.addLog(errorLog);

    if (!this.isProduction) {
      console.group(`🚨 Error Boundary: ${context}`);
      console.error('Error:', error);
      console.error('Component Stack:', errorInfo.componentStack);
      console.error('Full log:', errorLog);
      console.groupEnd();
    }
  }

  /**
   * Log API errors with request/response details
   */
  logApiError(
    error: any, 
    endpoint: string, 
    method: string = 'GET',
    requestData?: any
  ) {
    const additionalData = {
      endpoint,
      method,
      requestData,
      responseStatus: error.response?.status,
      responseData: error.response?.data,
      apiError: true
    };

    const errorLog = this.createErrorLog(
      error, 
      'error', 
      'API', 
      additionalData
    );
    
    this.addLog(errorLog);

    if (!this.isProduction) {
      console.group(`🌐 API Error: ${method} ${endpoint}`);
      console.error('Error:', error);
      console.error('Request data:', requestData);
      console.error('Response:', error.response);
      console.error('Full log:', errorLog);
      console.groupEnd();
    }
  }

  /**
   * Log user actions for error reproduction
   */
  logUserAction(action: string, context?: string, data?: any) {
    const actionLog = this.createErrorLog(
      `User action: ${action}`,
      'info',
      context || 'UserAction',
      {
        userAction: true,
        actionData: data
      }
    );

    this.addLog(actionLog);

    if (!this.isProduction) {
      console.info(`👤 User action: ${action}`, { context, data });
    }
  }

  /**
   * Get all logs (for debugging or reporting)
   */
  getLogs(): ErrorLog[] {
    return [...this.logs];
  }

  /**
   * Get logs filtered by level
   */
  getLogsByLevel(level: 'error' | 'warning' | 'info'): ErrorLog[] {
    return this.logs.filter(log => log.level === level);
  }

  /**
   * Get recent logs (last N logs)
   */
  getRecentLogs(count: number = 10): ErrorLog[] {
    return this.logs.slice(-count);
  }

  /**
   * Clear all logs
   */
  clearLogs() {
    this.logs = [];
  }

  /**
   * Export logs as JSON string (for support/debugging)
   */
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  /**
   * Send logs to error reporting service (placeholder)
   */
  async sendLogs(): Promise<void> {
    if (!this.isProduction) {
      console.info('Logs would be sent to error reporting service:', this.logs);
      return;
    }

    // In production, implement actual error reporting service integration
    // Examples: Sentry, LogRocket, Bugsnag, etc.
    try {
      // Example implementation:
      // await errorReportingService.sendLogs(this.logs);
      console.info('Logs sent to error reporting service');
    } catch (error) {
      console.error('Failed to send logs to error reporting service:', error);
    }
  }

  private createErrorLog(
    error: Error | string, 
    level: 'error' | 'warning' | 'info',
    context?: string, 
    additionalData?: Record<string, any>
  ): ErrorLog {
    const timestamp = new Date().toISOString();
    const id = `${timestamp}_${Math.random().toString(36).substr(2, 9)}`;
    
    let message: string;
    let stack: string | undefined;

    if (error instanceof Error) {
      message = error.message;
      stack = error.stack;
    } else {
      message = String(error);
    }

    return {
      id,
      timestamp,
      level,
      message,
      stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      context,
      additionalData
    };
  }

  private addLog(log: ErrorLog) {
    this.logs.push(log);
    
    // Keep only the most recent logs to prevent memory issues
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }
  }
}

// Create and export singleton instance
export const errorLogger = new ErrorLogger();

// Attach global error handlers
if (typeof window !== 'undefined') {
  // Catch unhandled JavaScript errors
  window.addEventListener('error', (event) => {
    errorLogger.logError(
      event.error || new Error(event.message),
      'UnhandledError',
      {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      }
    );
  });

  // Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    errorLogger.logError(
      event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
      'UnhandledPromiseRejection'
    );
  });
}

export default errorLogger;